import { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  useAddBranchAdminMutation,
  useLazyViewBranchAdminQuery,
  useUpdateBranchMutation,
} from "../../../../redux/api/solar-estimation/solarEstimationAPI";
import AdminBranchViewResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/admin-branch-view-response-dto";
import { ECountry } from "../../../../shared/oversight-core/enums/country";
import { OvstErrorCode } from "../../../../shared/oversight-core/enums/ovst-error-codes";
import { IHttpError } from "../../../../shared/oversight-core/interfaces/http-errror";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../shared/oversight-core/utils/toast";

interface IProps extends ModelContainerProps {
  show: boolean;
  branchId: string;
  setIsUpdateCurrentState: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IFormInput {
  countryCode: Option | [];
  name: string;
  longitude: number | null;
  latitude: number | null;
}

const defaultFormValues: IFormInput = {
  countryCode: [],
  name: "",
  longitude: null,
  latitude: null,
};

const countryList: Option[] = [
  { value: ECountry.KE, label: "Kenya" },
  { value: ECountry.TZ, label: "Tanzania" },
  { value: ECountry.UG, label: "Uganda" },
];

const AddBranchModal = (props: IProps) => {
  const { show, branchId, setIsUpdateCurrentState, ...rest } = props;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue,
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  const [addBranch, { isLoading: isLoadingAddBranch }] =
    useAddBranchAdminMutation();
  const [updateBranch, { isLoading: isLoadingUpdateBranch }] =
    useUpdateBranchMutation();
  const [triggerBranch, { isFetching }] = useLazyViewBranchAdminQuery();

  useEffect(() => {
    if (show && branchId) {
      triggerBranch({ branchId })
        .unwrap()
        .then((res: AdminBranchViewResponseDTO) => {
          setValue("name", res.branch.name);
          setValue("countryCode", {
            value: res.branch.countryCode,
            label: res.branch.country,
          });
          setValue("longitude", res.branch.longitude);
          setValue("latitude", res.branch.latitude);
        })
        .catch(() => {
          reset({ ...defaultFormValues });
        });
      return;
    }
    reset({ ...defaultFormValues });
  }, [branchId, show]);

  const onSubmit = (data: IFormInput) => {
    if (branchId) {
      updateBranch({
        branchId: branchId,
        name: data.name,
        countryCode: (data.countryCode as Option).value as ECountry,
        longitude: data.longitude ?? 0,
        latitude: data.latitude ?? 0,
      })
        .unwrap()
        .then(() => {
          showSuccessMessage("Branch updated successfully");
          props.onClose?.();
          setIsUpdateCurrentState((prev) => !prev);
        })
        .catch((error) => {
          if (
            error.status === 412 &&
            error.ovstErrorCode === OvstErrorCode.OVST_CONS_0001
          ) {
            showErrorMessage(
              "Given branch location details are not with in country limits."
            );
            return;
          }

          showErrorMessage("Failed to update branch");
        });
    } else {
      addBranch({
        name: data.name,
        countryCode: (data.countryCode as Option).value as ECountry,
        longitude: data.longitude ?? 0,
        latitude: data.latitude ?? 0,
      })
        .unwrap()
        .then(() => {
          showSuccessMessage("Branch added successfully");
          props.onClose?.();
          setIsUpdateCurrentState((prev) => !prev);
        })
        .catch((error: IHttpError) => {
          if (
            error.status === 412 &&
            error.ovstErrorCode === OvstErrorCode.OVST_CONS_0001
          ) {
            showErrorMessage(
              "Given branch location details are not with in country limits."
            );
            return;
          }

          showErrorMessage("Failed to add branch");
        });
    }
  };

  return (
    <ModalContainer
      title={`Add Branch`}
      {...rest}
      show={show}
      size="modal-lg"
      onConfirm={handleSubmit(onSubmit)}
      isLoading={isLoadingAddBranch || isLoadingUpdateBranch}
    >
      <>
        <AppSelect
          placeholder="Select Country"
          options={countryList}
          label="Country"
          control={control}
          name="countryCode"
          errors={errors}
          register={register("countryCode", {
            required: "Please select the country",
          })}
          isRequired
        />
        <AppInput
          className="mt-4"
          label="Branch Name"
          placeholder="Name"
          name="name"
          errors={errors}
          register={register("name", {
            required: "Please enter the branch name",
          })}
        />
        <AppInput
          type="number"
          className="mt-4"
          label="Longitude"
          placeholder="Longitude"
          name="longitude"
          errors={errors}
          register={register("longitude", {
            required: "Please enter the longitude",
          })}
        />
        <AppInput
          type="number"
          className="mt-4"
          label="Latitude"
          placeholder="Latitude"
          name="latitude"
          errors={errors}
          register={register("latitude", {
            required: "Please enter the latitude",
          })}
        />
        <SpinnerModal show={isFetching} />
      </>
    </ModalContainer>
  );
};

export default AddBranchModal;
